import { useCallback, useState } from 'react'
import ReactDOM from 'react-dom'

import Button from '../../common/Button'
import { formatCountySourceLabel, resolveCountyBeforeSave } from '../../../lib/countyResolution'

const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Above patient edit Modal (10060) and backdrop (10054)
    zIndex: 10080,
}

const cardStyle = {
    backgroundColor: 'var(--background, var(--white, #fff))',
    borderRadius: 8,
    padding: '16px 20px',
    minWidth: 320,
    maxWidth: 'min(480px, 92vw)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
}

const titleStyle = { margin: '0 0 8px', fontSize: 16, fontWeight: 600 }
const textStyle = { margin: '0 0 12px', fontSize: 14, lineHeight: 1.45, color: 'var(--subtitle, #555)' }
const noteStyle = {
    margin: '0 0 16px',
    fontSize: 13,
    lineHeight: 1.45,
    color: 'var(--subtitle, #555)',
    padding: '10px 12px',
    borderRadius: 6,
    backgroundColor: 'rgba(255, 193, 7, 0.12)',
}
const actionsStyle = { display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }

function formatCountyName(name) {
    const trimmed = `${name ?? ''}`.trim()
    return trimmed || 'Unknown'
}

export function CountyMismatchConfirmModal({
    selectedCountyName,
    suggestedCountyName,
    source,
    showLienNote = false,
    onUseSuggested,
    onKeepSelected,
    onCancel,
}) {
    const overlayRoot = typeof document !== 'undefined' ? document.getElementById('overlay-root') : null
    if (!overlayRoot) {
        return null
    }

    const sourceLabel = formatCountySourceLabel(source)
    const selectedLabel = formatCountyName(selectedCountyName)
    const suggestedLabel = formatCountyName(suggestedCountyName)

    return ReactDOM.createPortal(
        <div style={overlayStyle} role="presentation" onClick={onCancel}>
            <div
                style={cardStyle}
                role="dialog"
                aria-modal="true"
                aria-labelledby="county-mismatch-confirm-title"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 id="county-mismatch-confirm-title" style={titleStyle}>County does not match address</h3>
                <p style={textStyle}>
                    You selected: <strong>{selectedLabel}</strong>
                </p>
                <p style={textStyle}>
                    Address suggests: <strong>{suggestedLabel}</strong> ({sourceLabel})
                </p>
                {showLienNote && (
                    <p style={noteStyle}>
                        An incorrect county can cause lien submission to fail or file in the wrong county.
                    </p>
                )}
                <div style={actionsStyle}>
                    <Button click={onCancel} btnStyle="btn_white">Cancel</Button>
                    <Button click={onKeepSelected} btnStyle="btn_white">
                        {`Keep my selection (${selectedLabel})`}
                    </Button>
                    <Button click={onUseSuggested}>
                        {`Use suggested county (${suggestedLabel})`}
                    </Button>
                </div>
            </div>
        </div>,
        overlayRoot,
    )
}

/**
 * Returns a Promise resolving to 'suggested' | 'selected' | null (cancelled).
 */
export function useCountyMismatchConfirm({ showLienNote = false } = {}) {
    const [pending, setPending] = useState(null)

    const confirmCountyChoice = useCallback(({ selectedCountyName, suggestedCountyName, source }) => {
        return new Promise((resolve) => {
            setPending({
                selectedCountyName,
                suggestedCountyName,
                source,
                resolve,
            })
        })
    }, [])

    const finish = useCallback((result) => {
        setPending((current) => {
            if (current && typeof current.resolve === 'function') {
                current.resolve(result)
            }
            return null
        })
    }, [])

    const modal = pending ? (
        <CountyMismatchConfirmModal
            selectedCountyName={pending.selectedCountyName}
            suggestedCountyName={pending.suggestedCountyName}
            source={pending.source}
            showLienNote={showLienNote}
            onUseSuggested={() => finish('suggested')}
            onKeepSelected={() => finish('selected')}
            onCancel={() => finish(null)}
        />
    ) : null

    return { confirmCountyChoice, modal }
}

export function useContactCountyConfirmation({ showLienNote = false } = {}) {
    const { confirmCountyChoice, modal } = useCountyMismatchConfirm({ showLienNote })

    const confirmCountyOnSave = useCallback(async ({
        token,
        originalContact,
        formContact,
        selectedCountyId = null,
        selectedCountyLabel = '',
        countiesOptions,
        onSuggestedCounty,
    }) => {
        return resolveCountyBeforeSave({
            token,
            originalContact,
            formContact,
            selectedCountyId,
            selectedCountyLabel,
            confirmCountyChoice,
            countiesOptions,
            onSuggestedCounty,
        })
    }, [confirmCountyChoice])

    return { confirmCountyOnSave, modal }
}
